import { format, type Locale } from 'date-fns'
import { enUS, it } from 'date-fns/locale'
import { type RefObject, useMemo } from 'react'
import {
	type Control,
	Controller,
	type FieldErrors,
	useWatch
} from 'react-hook-form'
import { InputHelpText } from '@/components/ui/input-help-text'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import type { ComboboxOption } from '@/features/marquee/components/combobox-field'
import {
	COLUMN_WIDTH,
	Field,
	useErrorText
} from '@/features/marquee/components/event-form-atoms'
import type { EventFormValues } from '@/features/marquee/form-schemas'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

/** i18n language → date-fns locale for localized time labels. */
const TIME_LOCALES: Record<string, Locale> = { en: enUS, it }

interface TimeRangeFieldsProps {
	control: Control<EventFormValues>
	errors: FieldErrors<EventFormValues>
	/**
	 * The event's IANA zone, known on edit only (it is derived from the market
	 * server-side, so a create has nothing to show until the event exists).
	 */
	timeZone?: string
	/** Where to portal the slot lists. */
	popupContainer?: RefObject<HTMLElement | null>
}

/**
 * Start + end time pickers in 15-minute localized slots. Each side disables the
 * slots that would invert the range (start ≥ end and vice versa).
 *
 * The times are wall-clock in the event's market, not in the browser's zone —
 * markets no longer all resolve to Eastern, so the hint says whose clock this
 * is, naming the zone once the backend has resolved it.
 */
export function TimeRangeFields({
	control,
	errors,
	timeZone,
	popupContainer
}: TimeRangeFieldsProps) {
	const { t, i18n } = useTranslation()
	const startTimeValue = useWatch({ control, name: 'startTime' })
	const endTimeValue = useWatch({ control, name: 'endTime' })

	const timeOptions = useMemo(() => {
		const locale = TIME_LOCALES[i18n.language] ?? enUS
		const slots: ComboboxOption[] = []
		for (let hour = 0; hour < 24; hour += 1) {
			for (let minute = 0; minute < 60; minute += 15) {
				const value = `${String(hour).padStart(2, '0')}:${String(
					minute
				).padStart(2, '0')}`
				const label = format(new Date(2000, 0, 1, hour, minute), 'p', {
					locale
				})
				slots.push({ value, label })
			}
		}
		return slots
	}, [i18n.language])

	// The stored value is the 24h "HH:mm"; the trigger must show the localized
	// label (base-ui renders the raw value unless given a formatter). The empty
	// case is handled here too: base-ui ignores `SelectValue`'s `placeholder`
	// prop whenever children is a function, so the fallback has to come from the
	// function itself.
	const timeLabel = (value: string | null, placeholder: string) => {
		if (!value) {
			return placeholder
		}
		return (
			timeOptions.find((option) => option.value === value)?.label ?? value
		)
	}

	const errorText = useErrorText()

	const zoneHint = timeZone
		? t('marquee:form_time_zone_hint', { timeZone })
		: t('marquee:form_time_zone_hint_unknown')

	return (
		<div className='flex flex-col gap-1.5'>
			<div className='flex flex-wrap gap-6'>
				<Controller
					control={control}
					name='startTime'
					render={({ field }) => (
						<Field
							className={COLUMN_WIDTH}
							error={errorText(errors.startTime?.message)}
							label={t('marquee:form_start_time_label')}
							required
						>
							<Select
								onValueChange={(value) =>
									field.onChange(value ?? '')
								}
								value={field.value || null}
							>
								<SelectTrigger
									className={cn(
										'w-full',
										errors.startTime && 'border-danger-300'
									)}
									nativeButton={false}
								>
									<SelectValue>
										{(value: string | null) =>
											timeLabel(
												value,
												t(
													'marquee:form_start_time_placeholder'
												)
											)
										}
									</SelectValue>
								</SelectTrigger>
								<SelectContent
									className='max-h-[280px] min-w-[var(--anchor-width)]'
									container={popupContainer}
								>
									{timeOptions.map((option) => (
										<SelectItem
											disabled={
												!!endTimeValue &&
												option.value >= endTimeValue
											}
											key={option.value}
											value={option.value}
										>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
					)}
				/>

				<Controller
					control={control}
					name='endTime'
					render={({ field }) => (
						<Field
							className={COLUMN_WIDTH}
							error={errorText(errors.endTime?.message)}
							label={t('marquee:form_end_time_label')}
							required
						>
							<Select
								onValueChange={(value) =>
									field.onChange(value ?? '')
								}
								value={field.value || null}
							>
								<SelectTrigger
									className={cn(
										'w-full',
										errors.endTime && 'border-danger-300'
									)}
									nativeButton={false}
								>
									<SelectValue>
										{(value: string | null) =>
											timeLabel(
												value,
												t(
													'marquee:form_end_time_placeholder'
												)
											)
										}
									</SelectValue>
								</SelectTrigger>
								<SelectContent
									className='max-h-[280px] min-w-[var(--anchor-width)]'
									container={popupContainer}
								>
									{timeOptions.map((option) => (
										<SelectItem
											disabled={
												!!startTimeValue &&
												option.value <= startTimeValue
											}
											key={option.value}
											value={option.value}
										>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
					)}
				/>
			</div>
			<InputHelpText helpText={zoneHint} />
		</div>
	)
}
